import { Button, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import { emailValidator } from "@entrilia/shared/utilities/validators";
import { api } from "../../../api/operationsApi";
import { useExecutionState } from "../../../hooks/useExecutionState";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import PageTitle from "../../common/PageTitle";

const resetGuestUserMfa = withErrorHandling(api.entriliaUsers.resetGuestUserMfa);

const ResetGuestUserMfaContent = () => {
  const { executionState, setExecutionState } = useExecutionState();
  const [userEmail, setUserEmail] = useState("");
  const [emailValidationResult, setEmailValidationResult] = useState<ValidationResult>();

  const handleResetMfa = async () => {
    if (!userEmail.trim()) {
      return;
    }

    const sanitizedEmail = sanitizeEmail(userEmail);
    const validationResult = emailValidator(sanitizedEmail);
    setEmailValidationResult(validationResult);
    if (!validationResult.isValid) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const [resp, error] = await resetGuestUserMfa({
      email: sanitizedEmail,
    });

    if (error) {
      setExecutionState({ status: "error", error });
      return;
    }

    if (!resp.success) {
      setExecutionState({
        status: "error",
        text: "Failed",
        errorMessage: `Failed to reset MFA for user ${resp.email}.`,
      });
      return;
    }

    setExecutionState({
      status: "success",
      text: "MFA Reset Successful",
      details: [
        `MFA settings have been successfully reset for guest user ${resp.email} in the Entrilia Azure AD tenant.`,
        "The user will need to re-register their MFA methods on next sign-in.",
      ],
    });
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle title="Reset Guest User MFA" subtitle="🔐 What this does" defaultExpanded>
        Reset MFA authentication methods for guest users in the Entrilia Azure AD directory
        <br />
        <br />
        • Removes all MFA authentication methods (phone, authenticator app, etc.)
        <br />
        • Revokes all active sign-in sessions
        <br />
        • Forces the user to re-register MFA on next sign-in
        <br />
        <br />
        ⚠️ <strong>Important:</strong> This action only works for <strong>guest users</strong>. Entrilia employees
        cannot have their MFA reset through this interface.
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="Guest User Email"
          value={userEmail}
          onChange={(e) => setUserEmail(e.target.value)}
          error={emailValidationResult?.isValid === false}
          helperText={emailValidationResult?.error}
        />
        <FieldHelp>
          Email address of the guest user whose MFA settings need to be reset. This must be a guest user in the Entrilia
          Azure Active Directory.
        </FieldHelp>
      </Stack>
      <Button
        variant="contained"
        color="primary"
        onClick={handleResetMfa}
        loading={executionState.status === "in-progress"}
        disabled={!userEmail.trim()}
      >
        Reset MFA
      </Button>
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
    </Stack>
  );
};

export default ResetGuestUserMfaContent;
